/**
 * ContentItemDetail - Zijpaneel (Drawer) voor bekijken/bewerken van een content item
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Drawer, Box, Typography, TextField, Button, Chip, Select, MenuItem,
  FormControl, InputLabel, Divider, IconButton, Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon, Delete as DeleteIcon, Save as SaveIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  IDEA: { label: 'Idee', color: '#6b7280', bg: '#f3f4f6' },
  CONCEPT: { label: 'Concept', color: '#8b5cf6', bg: '#ede9fe' },
  SHOOT_PLANNING: { label: 'Draaidag plannen', color: '#d97706', bg: '#fef3c7' },
  SHOOT_SCHEDULED: { label: 'Draaidag ingepland', color: '#ea580c', bg: '#ffedd5' },
  IN_PRODUCTION: { label: 'In Productie', color: '#2563eb', bg: '#dbeafe' },
  REVIEW: { label: 'Review', color: '#7c3aed', bg: '#ede9fe' },
  APPROVED: { label: 'Goedgekeurd', color: '#059669', bg: '#d1fae5' },
  ARCHIVED: { label: 'Gearchiveerd', color: '#9ca3af', bg: '#f3f4f6' },
};

const POST_STATUS_CONFIG = {
  INTERN: { label: 'Intern', color: '#6b7280' },
  CONCEPT: { label: 'Concept', color: '#8b5cf6' },
  SCHEDULED: { label: 'Ingepland', color: '#2563eb' },
  PUBLISHED: { label: 'Geplaatst', color: '#059669' },
  ARCHIVED: { label: 'Gearchiveerd', color: '#9ca3af' },
};

const APPROVAL_CONFIG = {
  NOT_OFFERED: { label: 'Nog niet', color: '#9ca3af' },
  IN_REVIEW: { label: 'In review', color: '#f59e0b' },
  APPROVED: { label: 'Goedgekeurd', color: '#10b981' },
  REJECTED: { label: 'Afgekeurd', color: '#ef4444' },
};

const CHANNELS = ['Instagram Post', 'Instagram Story', 'Instagram Reels', 'Facebook Post', 'TikTok', 'YouTube', 'YouTube Shorts', 'LinkedIn', 'Threads Post'];
const CONTENT_TYPES = ['video', 'afbeelding', 'text_visual', 'animatie', 'anders'];
const FUNNEL_STAGES = ['TOFU', 'MOFU', 'BOFU'];

// Valid next statuses per status
const STATUS_TRANSITIONS = {
  IDEA: ['CONCEPT'],
  CONCEPT: ['SHOOT_PLANNING', 'IN_PRODUCTION'],
  SHOOT_PLANNING: ['SHOOT_SCHEDULED'],
  SHOOT_SCHEDULED: ['IN_PRODUCTION'],
  IN_PRODUCTION: ['REVIEW'],
  REVIEW: ['APPROVED', 'IN_PRODUCTION'],
  APPROVED: ['ARCHIVED'],
  ARCHIVED: [],
};

const POST_TRANSITIONS = {
  INTERN: ['CONCEPT'],
  CONCEPT: ['SCHEDULED'],
  SCHEDULED: ['PUBLISHED'],
  PUBLISHED: ['ARCHIVED'],
  ARCHIVED: [],
};

const EMPTY_FORM = {
  title: '', description: '', copyText: '', status: 'IDEA', postStatus: 'INTERN',
  channels: [], contentType: '', campaignTag: '', funnelStage: '', ownerEmail: '', ownerName: '',
  plannedDate: '', publishedDate: '', deadline: '', shootDate: '', postTime: '',
  approvalConcept: 'NOT_OFFERED', approvalContent: 'NOT_OFFERED', approvalPost: 'NOT_OFFERED',
  eventId: '', url: '', thumbnailUrl: '', notes: '',
};

// ─── Section label ──────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <Typography variant="overline" sx={{ color: '#6b7280', fontWeight: 700, mt: 2, mb: 0.5, display: 'block' }}>
      {children}
    </Typography>
  );
}

// ─── Approval chip with actions ─────────────────────────────────────────────

function ApprovalField({ label, value, onChange }) {
  const cfg = APPROVAL_CONFIG[value] || APPROVAL_CONFIG.NOT_OFFERED;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        <Chip label={cfg.label} size="small" sx={{ bgcolor: `${cfg.color}18`, color: cfg.color, fontWeight: 600 }} />
        {value === 'NOT_OFFERED' && (
          <Button size="small" variant="outlined" onClick={() => onChange('IN_REVIEW')} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
            Naar review
          </Button>
        )}
        {value === 'IN_REVIEW' && (
          <>
            <Button size="small" color="success" variant="outlined" onClick={() => onChange('APPROVED')} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
              Goedkeuren
            </Button>
            <Button size="small" color="error" variant="outlined" onClick={() => onChange('REJECTED')} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
              Afkeuren
            </Button>
          </>
        )}
        {value === 'REJECTED' && (
          <Button size="small" variant="outlined" onClick={() => onChange('IN_REVIEW')} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
            Opnieuw review
          </Button>
        )}
      </Box>
    </Box>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export default function ContentItemDetail({ open, item, events = [], onClose, onSaved }) {
  const isCreate = !item;
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({
        ...EMPTY_FORM,
        ...item,
        channels: item.channels || [],
        plannedDate: item.plannedDate?.slice(0, 10) || '',
        publishedDate: item.publishedDate?.slice(0, 10) || '',
        deadline: item.deadline?.slice(0, 10) || '',
        shootDate: item.shootDate?.slice(0, 10) || '',
        postTime: item.postTime || '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [item, open]);

  const set = (field) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  const handleStatusTransition = (newStatus) => setForm((prev) => ({ ...prev, status: newStatus }));
  const handlePostTransition = (newPost) => setForm((prev) => ({ ...prev, postStatus: newPost }));

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Titel is verplicht'); return; }
    setSaving(true);
    try {
      const url = isCreate
        ? `${BACKEND_URL}/Api/marketing/content-items`
        : `${BACKEND_URL}/Api/marketing/content-items/${item.id}`;
      const method = isCreate ? 'POST' : 'PATCH';
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Opslaan mislukt');
      toast.success(isCreate ? 'Item aangemaakt' : 'Item opgeslagen');
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!item?.id) return;
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/marketing/content-items/${item.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'ARCHIVED' }),
      });
      if (!res.ok) throw new Error('Archiveren mislukt');
      toast.success('Item gearchiveerd');
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const nextStatuses = STATUS_TRANSITIONS[form.status] || [];
  const nextPostStatuses = POST_TRANSITIONS[form.postStatus] || [];
  const statusCfg = STATUS_CONFIG[form.status] || {};
  const postCfg = POST_STATUS_CONFIG[form.postStatus] || {};

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 520, p: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {isCreate ? 'Nieuw content item' : 'Content item'}
        </Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      {/* ─── Basis ──────────────────────────────────────────────────── */}
      <SectionLabel>Basis</SectionLabel>
      <TextField label="Titel" required fullWidth size="small" value={form.title} onChange={set('title')} sx={{ mb: 1.5 }} />
      <TextField label="Beschrijving" fullWidth multiline rows={2} size="small" value={form.description} onChange={set('description')} sx={{ mb: 1.5 }} />
      <TextField label="Copy tekst" fullWidth multiline rows={3} size="small" value={form.copyText} onChange={set('copyText')} sx={{ mb: 1.5 }} />

      <Divider sx={{ my: 1 }} />

      {/* ─── Planning ───────────────────────────────────────────────── */}
      <SectionLabel>Planning</SectionLabel>

      {/* Production status */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="caption" sx={{ width: 50 }}>Status</Typography>
        <Chip label={statusCfg.label || form.status} size="small" sx={{ bgcolor: statusCfg.bg, color: statusCfg.color, fontWeight: 600 }} />
        {nextStatuses.map((ns) => (
          <Tooltip key={ns} title={STATUS_CONFIG[ns]?.label}>
            <IconButton size="small" onClick={() => handleStatusTransition(ns)} sx={{ border: '1px solid #e5e7eb' }}>
              <ArrowIcon sx={{ fontSize: 14, color: STATUS_CONFIG[ns]?.color }} />
            </IconButton>
          </Tooltip>
        ))}
      </Box>

      {/* Post status */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography variant="caption" sx={{ width: 50 }}>Post</Typography>
        <Chip label={postCfg.label || form.postStatus} size="small" sx={{ bgcolor: `${postCfg.color}18`, color: postCfg.color, fontWeight: 600 }} />
        {nextPostStatuses.map((ns) => (
          <Tooltip key={ns} title={POST_STATUS_CONFIG[ns]?.label}>
            <IconButton size="small" onClick={() => handlePostTransition(ns)} sx={{ border: '1px solid #e5e7eb' }}>
              <ArrowIcon sx={{ fontSize: 14, color: POST_STATUS_CONFIG[ns]?.color }} />
            </IconButton>
          </Tooltip>
        ))}
      </Box>

      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
        <InputLabel>Kanalen</InputLabel>
        <Select multiple value={form.channels} label="Kanalen" onChange={set('channels')}
          renderValue={(sel) => <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>{sel.map((s) => <Chip key={s} label={s} size="small" />)}</Box>}>
          {CHANNELS.map((ch) => <MenuItem key={ch} value={ch}>{ch}</MenuItem>)}
        </Select>
      </FormControl>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>Content type</InputLabel>
          <Select value={form.contentType} label="Content type" onChange={set('contentType')}>
            <MenuItem value="">-</MenuItem>
            {CONTENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>Funnel</InputLabel>
          <Select value={form.funnelStage} label="Funnel" onChange={set('funnelStage')}>
            <MenuItem value="">-</MenuItem>
            {FUNNEL_STAGES.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <TextField label="Campagne tag" fullWidth size="small" value={form.campaignTag} onChange={set('campaignTag')} sx={{ mb: 1.5 }} />
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
        <TextField label="Eigenaar email" size="small" sx={{ flex: 1 }} value={form.ownerEmail} onChange={set('ownerEmail')} />
        <TextField label="Eigenaar naam" size="small" sx={{ flex: 1 }} value={form.ownerName} onChange={set('ownerName')} />
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* ─── Datums ─────────────────────────────────────────────────── */}
      <SectionLabel>Datums</SectionLabel>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
        <TextField label="Plandatum" type="date" size="small" value={form.plannedDate} onChange={set('plannedDate')} InputLabelProps={{ shrink: true }} />
        <TextField label="Publicatiedatum" type="date" size="small" value={form.publishedDate} onChange={set('publishedDate')} InputLabelProps={{ shrink: true }} />
        <TextField label="Deadline" type="date" size="small" value={form.deadline} onChange={set('deadline')} InputLabelProps={{ shrink: true }} />
        <TextField label="Draaidag" type="date" size="small" value={form.shootDate} onChange={set('shootDate')} InputLabelProps={{ shrink: true }} />
        <TextField label="Post tijd" type="time" size="small" value={form.postTime} onChange={set('postTime')} InputLabelProps={{ shrink: true }} />
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* ─── Akkoord ────────────────────────────────────────────────── */}
      <SectionLabel>Akkoord</SectionLabel>
      <ApprovalField label="Concept akkoord" value={form.approvalConcept} onChange={(v) => setForm((p) => ({ ...p, approvalConcept: v }))} />
      <ApprovalField label="Content akkoord" value={form.approvalContent} onChange={(v) => setForm((p) => ({ ...p, approvalContent: v }))} />
      <ApprovalField label="Post akkoord" value={form.approvalPost} onChange={(v) => setForm((p) => ({ ...p, approvalPost: v }))} />

      <Divider sx={{ my: 1 }} />

      {/* ─── Links ──────────────────────────────────────────────────── */}
      <SectionLabel>Links</SectionLabel>
      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
        <InputLabel>Event</InputLabel>
        <Select value={form.eventId || ''} label="Event" onChange={set('eventId')}>
          <MenuItem value="">Geen</MenuItem>
          {events.map((ev) => (
            <MenuItem key={ev.id} value={ev.id}>
              {ev.name} ({ev.date ? new Date(ev.date).toLocaleDateString('nl-NL') : '-'})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField label="URL" fullWidth size="small" value={form.url} onChange={set('url')} sx={{ mb: 1.5 }} />
      <TextField label="Thumbnail URL" fullWidth size="small" value={form.thumbnailUrl} onChange={set('thumbnailUrl')} sx={{ mb: 1.5 }} />
      <TextField label="Notities" fullWidth multiline rows={2} size="small" value={form.notes} onChange={set('notes')} sx={{ mb: 2 }} />

      {/* ─── Actions ────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 1.5, mt: 'auto' }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving} sx={{ flex: 1 }}>
          {isCreate ? 'Aanmaken' : 'Opslaan'}
        </Button>
        {!isCreate && (
          <Tooltip title="Archiveren">
            <IconButton color="error" onClick={handleArchive}><DeleteIcon /></IconButton>
          </Tooltip>
        )}
      </Box>
    </Drawer>
  );
}
